'use client';

import React, { type JSX, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '@videoblocks/design-system';
import { DownloadBottom } from '@videoblocks/react-icons';

interface ResultCardHoverOverlayProps {
    title: string;
    /** Rendered ahead of the title, e.g. a video's duration. */
    badge?: ReactNode;
}

/** Title band revealed on hover; the card root must carry Tailwind's `group` class. */
export function ResultCardHoverOverlay({ title, badge }: ResultCardHoverOverlayProps): JSX.Element {
    return (
        <>
            <div
                className="absolute inset-0 rounded-md pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 40%)',
                }}
            />
            <div className="flex space-x-2 items-center absolute top-0 left-0 px-4 pt-4 w-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                {badge}
                <h3 className="truncate font-semibold text-sm text-white m-0 leading-5">{title}</h3>
            </div>
        </>
    );
}

interface DownloadActionProps {
    isPortrait: boolean;
    label: string;
    disabled: boolean;
    onClick: (e: React.MouseEvent) => void;
}

const POSITION_CLASS_NAME =
    'absolute left-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-auto z-[1]';
const SHARED_BUTTON_CLASS_NAME =
    'flex items-center text-white bg-gray-900 rounded-full border-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed';

export function DownloadAction({ isPortrait, label, disabled, onClick }: DownloadActionProps): JSX.Element {
    // A labelled pill is too wide for the narrow vertical card, so collapse to an icon button and
    // surface the label through a tooltip instead.
    if (isPortrait) {
        return (
            <div className={POSITION_CLASS_NAME}>
                <Tooltip
                    title={label}
                    variant="dark"
                    trigger={
                        <button
                            type="button"
                            aria-label={label}
                            onClick={onClick}
                            disabled={disabled}
                            className={twMerge(SHARED_BUTTON_CLASS_NAME, 'justify-center w-10 h-10')}
                        >
                            <DownloadBottom fill="white" width={14} height={14} />
                        </button>
                    }
                />
            </div>
        );
    }

    return (
        <div className={POSITION_CLASS_NAME}>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                className={twMerge(SHARED_BUTTON_CLASS_NAME, 'gap-1.5 text-xs font-medium px-4 py-3')}
            >
                <DownloadBottom fill="white" width={14} height={14} />
                {label}
            </button>
        </div>
    );
}
